import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabaseClient";
import {
  isTesterCampaign,
  loadClaimableRestaurants,
  loadCommunitySnapshot,
  requestTesterInterest,
  submitRestaurantClaim,
  syncCommunityProfile,
  trackCommunityOpened,
  trackSep10Landing,
} from "./sep10Community";

const shell = {
  position: "fixed", inset: 0, zIndex: 2147482000, background: "rgba(0,0,0,.68)",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
};
const card = {
  width: "min(560px, 100%)", maxHeight: "88vh", overflowY: "auto", background: "#111",
  color: "#f7f4ed", border: "1px solid #353535", borderRadius: 16, padding: 20,
  boxShadow: "0 20px 70px rgba(0,0,0,.5)", fontFamily: "inherit",
};
const button = {
  border: 0, borderRadius: 10, padding: "11px 14px", fontWeight: 800, cursor: "pointer",
  background: "#ef3038", color: "#fff",
};
const secondary = { ...button, background: "#2b2b2b" };
const input = {
  width: "100%", boxSizing: "border-box", background: "#191919", color: "#fff", border: "1px solid #444",
  borderRadius: 9, padding: "10px 11px", marginTop: 5,
};

const COMMUNITY_POSITION_KEY = "venaqqi_admin_community_button_position_v1";
const COMMUNITY_EDGE_GAP = 8;
const COMMUNITY_DRAG_THRESHOLD = 5;

export const clampCommunityPosition = (position, buttonSize, viewport) => {
  const width = Math.max(0, Number(buttonSize?.width) || 0);
  const height = Math.max(0, Number(buttonSize?.height) || 0);
  const viewportWidth = Math.max(0, Number(viewport?.width) || 0);
  const viewportHeight = Math.max(0, Number(viewport?.height) || 0);
  const maxX = Math.max(COMMUNITY_EDGE_GAP, viewportWidth - width - COMMUNITY_EDGE_GAP);
  const maxY = Math.max(COMMUNITY_EDGE_GAP, viewportHeight - height - COMMUNITY_EDGE_GAP);
  return {
    x: Math.min(maxX, Math.max(COMMUNITY_EDGE_GAP, Number(position?.x) || COMMUNITY_EDGE_GAP)),
    y: Math.min(maxY, Math.max(COMMUNITY_EDGE_GAP, Number(position?.y) || COMMUNITY_EDGE_GAP)),
  };
};

const defaultCommunityPosition = () => ({
  x: typeof window !== "undefined" ? Math.max(COMMUNITY_EDGE_GAP, window.innerWidth - 112) : COMMUNITY_EDGE_GAP,
  y: 78,
});

const readCommunityPosition = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(COMMUNITY_POSITION_KEY) || "null");
    if (Number.isFinite(stored?.x) && Number.isFinite(stored?.y)) return stored;
  } catch { /* fall back to default */ }
  return defaultCommunityPosition();
};

const benefitLabels = entitlements => [
  entitlements?.early_access ? "Acceso anticipado" : null,
  entitlements?.priority_moderation ? "Prioridad de moderación" : null,
  entitlements?.validation_tools ? "Herramientas de validación" : null,
  Number(entitlements?.priority_review_bonus || 0) > 0 ? `Prioridad +${entitlements.priority_review_bonus}` : null,
].filter(Boolean);

export default function Sep10GrowthCommunityLayer() {
  const testerCampaign = useMemo(() => isTesterCampaign(), []);
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(testerCampaign);
  const [snapshot, setSnapshot] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [communityPosition, setCommunityPosition] = useState(readCommunityPosition);
  const [claim, setClaim] = useState({ restaurantId: "", claimantRole: "owner", evidenceType: "official_social", evidenceNote: "", contactPreference: "in_app" });
  const communityButtonRef = useRef(null);
  const positionRef = useRef(communityPosition);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  const setFloatingPosition = useCallback(next => {
    positionRef.current = next;
    setCommunityPosition(next);
  }, []);

  const clampCurrentPosition = useCallback(position => {
    const rect = communityButtonRef.current?.getBoundingClientRect?.() || { width: 96, height: 36 };
    return clampCommunityPosition(position, rect, { width: window.innerWidth, height: window.innerHeight });
  }, []);

  const persistCommunityPosition = useCallback(position => {
    try { localStorage.setItem(COMMUNITY_POSITION_KEY, JSON.stringify(position)); } catch { /* storage unavailable */ }
  }, []);

  const refresh = useCallback(async currentUser => {
    if (!currentUser) { setSnapshot(null); return; }
    await syncCommunityProfile();
    const [nextSnapshot, places] = await Promise.all([loadCommunitySnapshot(), loadClaimableRestaurants()]);
    setSnapshot(nextSnapshot);
    setRestaurants(places);
  }, []);

  useEffect(() => {
    void trackSep10Landing();
    let active = true;
    void supabase.auth.getUser().then(({ data }) => {
      if (!active) return;
      const current = data?.user || null;
      setUser(current);
      void refresh(current).catch(() => {});
    });
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      const current = session?.user || null;
      setUser(current);
      void refresh(current).catch(() => {});
    });
    return () => { active = false; authListener?.subscription?.unsubscribe?.(); };
  }, [refresh]);

  useEffect(() => {
    const clampAndSave = () => {
      const next = clampCurrentPosition(positionRef.current);
      setFloatingPosition(next);
      persistCommunityPosition(next);
    };
    const timer = setTimeout(clampAndSave, 0);
    window.addEventListener("resize", clampAndSave);
    return () => { clearTimeout(timer); window.removeEventListener("resize", clampAndSave); };
  }, [clampCurrentPosition, persistCommunityPosition, setFloatingPosition]);

  const openCommunity = () => {
    setOpen(true);
    setMessage("");
    setError("");
    void trackCommunityOpened();
  };

  const beginCommunityDrag = event => {
    if (event.button !== undefined && event.button !== 0) return;
    const start = positionRef.current;
    dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, originX: start.x, originY: start.y, moved: false };
    suppressClickRef.current = false;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveCommunityButton = event => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) >= COMMUNITY_DRAG_THRESHOLD) drag.moved = true;
    if (!drag.moved) return;
    event.preventDefault();
    const next = clampCurrentPosition({ x: drag.originX + dx, y: drag.originY + dy });
    setFloatingPosition(next);
  };

  const endCommunityDrag = event => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (drag.moved) {
      suppressClickRef.current = true;
      persistCommunityPosition(positionRef.current);
    }
  };

  const handleCommunityClick = event => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      return;
    }
    openCommunity();
  };

  const askTester = async () => {
    if (!user) {
      setMessage("Inicia sesión con tu cuenta de Venaqqi y vuelve a tocar ‘Quiero participar’.");
      return;
    }
    setBusy("tester"); setMessage(""); setError("");
    try {
      await requestTesterInterest();
      await refresh(user);
      setMessage("Solicitud guardada. Ya quedó registrada para revisión.");
    } catch (e) {
      setError(e?.message || "No se pudo guardar la solicitud.");
    } finally { setBusy(""); }
  };

  const sendClaim = async event => {
    event.preventDefault();
    if (!user) { setMessage("Inicia sesión antes de enviar un claim."); return; }
    setBusy("claim"); setMessage(""); setError("");
    try {
      await submitRestaurantClaim({ ...claim, restaurantId: Number(claim.restaurantId) });
      await refresh(user);
      setMessage("Claim guardado. Se revisará la evidencia antes de reconocer al propietario o representante.");
      setClaim(current => ({ ...current, evidenceNote: "" }));
    } catch (e) {
      setError(e?.message || "No se pudo guardar el claim.");
    } finally { setBusy(""); }
  };

  const badgeList = Array.isArray(snapshot?.badges) ? snapshot.badges : [];
  const benefits = benefitLabels(snapshot?.entitlements);
  const origin = snapshot?.profile?.origin_class === "tester" ? "Tester" : snapshot?.profile?.origin_class === "colado" ? "Los Colados" : null;
  const pendingTester = snapshot?.tester_interest?.status === "pending";

  return <>
    <button
      ref={communityButtonRef}
      type="button"
      onClick={handleCommunityClick}
      onPointerDown={beginCommunityDrag}
      onPointerMove={moveCommunityButton}
      onPointerUp={endCommunityDrag}
      onPointerCancel={endCommunityDrag}
      aria-label="Abrir comunidad Venaqqi"
      data-analytics-action="open-community"
      style={{ ...button, position: "fixed", left: communityPosition.x, top: communityPosition.y, zIndex: 2147481000, padding: "8px 11px", fontSize: 13, boxShadow: "0 6px 24px rgba(0,0,0,.3)", touchAction: "none", userSelect: "none" }}
    >Comunidad</button>

    {open && <div style={shell} role="dialog" aria-modal="true" aria-label="Comunidad Venaqqi">
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 12, color: "#ff6970", fontWeight: 800, letterSpacing: ".08em" }}>VENAQQI · COMUNIDAD</div>
            <h2 style={{ margin: "4px 0 8px", fontSize: 25 }}>Participa sin inflar el catálogo</h2>
          </div>
          <button type="button" aria-label="Cerrar comunidad" onClick={() => setOpen(false)} style={{ ...secondary, padding: "7px 10px" }}>×</button>
        </div>

        {testerCampaign && <div style={{ background: "#211719", border: "1px solid #6b2b30", borderRadius: 10, padding: 12, margin: "12px 0" }}>
          <strong>¿Comentaste TESTER?</strong><br />Tu llegada ya conserva el origen de campaña. Puedes solicitar participar sin que eso te autorice automáticamente.
          <div style={{ marginTop: 10 }}><button type="button" disabled={busy === "tester" || pendingTester} onClick={askTester} style={button}>{pendingTester ? "Solicitud pendiente" : busy === "tester" ? "Guardando…" : "Quiero participar"}</button></div>
        </div>}

        {!testerCampaign && <div style={{ margin: "12px 0" }}>
          <button type="button" disabled={busy === "tester" || pendingTester} onClick={askTester} style={secondary}>{pendingTester ? "Solicitud TESTER pendiente" : busy === "tester" ? "Guardando…" : "Quiero ser tester"}</button>
        </div>}

        {user ? <>
          <div style={{ display: "grid", gap: 8, margin: "14px 0" }}>
            {origin && <div><strong>Origen de comunidad:</strong> {origin}</div>}
            <div><strong>Insignias:</strong> {badgeList.length ? badgeList.map(x => x.label || x.code).join(" · ") : "Todavía ninguna"}</div>
            <div><strong>Beneficios activos:</strong> {benefits.length ? benefits.join(" · ") : "Ninguno todavía"}</div>
            <div><strong>Aportes aceptados:</strong> {Number(snapshot?.accepted_contributions || 0)}</div>
          </div>

          <hr style={{ border: 0, borderTop: "1px solid #333", margin: "18px 0" }} />
          <h3 style={{ margin: "0 0 8px" }}>¿Eres dueño o responsable de un restaurante?</h3>
          <p style={{ color: "#bbb", marginTop: 0, fontSize: 14 }}>El claim se guarda primero y después se verifica. Ningún claim modifica el catálogo ni la recomendación de Don D’ por sí solo.</p>
          <form onSubmit={sendClaim}>
            <label>Restaurante
              <select required value={claim.restaurantId} onChange={e => setClaim({ ...claim, restaurantId: e.target.value })} style={input}>
                <option value="">Selecciona…</option>
                {restaurants.map(place => <option key={place.id} value={place.id}>{place.name}{place.location ? ` — ${place.location}` : ""}</option>)}
              </select>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <label>Relación
                <select value={claim.claimantRole} onChange={e => setClaim({ ...claim, claimantRole: e.target.value })} style={input}>
                  <option value="owner">Propietario</option><option value="manager">Gerente</option><option value="authorized_representative">Representante autorizado</option>
                </select>
              </label>
              <label>Evidencia
                <select value={claim.evidenceType} onChange={e => setClaim({ ...claim, evidenceType: e.target.value })} style={input}>
                  <option value="official_social">Red oficial</option><option value="business_email">Correo del negocio</option><option value="in_person">Verificación presencial</option><option value="other">Otra</option>
                </select>
              </label>
            </div>
            <label style={{ display: "block", marginTop: 10 }}>Cómo podemos verificarlo
              <textarea required minLength={10} maxLength={1200} rows={3} value={claim.evidenceNote} onChange={e => setClaim({ ...claim, evidenceNote: e.target.value })} style={input} placeholder="Describe brevemente la evidencia. No incluyas documentos sensibles." />
            </label>
            <label style={{ display: "block", marginTop: 10 }}>Contacto preferido
              <select value={claim.contactPreference} onChange={e => setClaim({ ...claim, contactPreference: e.target.value })} style={input}>
                <option value="in_app">Dentro de Venaqqi</option><option value="email">Correo de mi cuenta</option>
              </select>
            </label>
            <button type="submit" disabled={busy === "claim" || !claim.restaurantId} style={{ ...button, marginTop: 12 }}>{busy === "claim" ? "Guardando…" : "Enviar claim"}</button>
          </form>
        </> : <p style={{ color: "#ccc" }}>Inicia sesión para ver tu origen, insignias, beneficios, solicitar ser tester o reclamar un restaurante.</p>}

        <hr style={{ border: 0, borderTop: "1px solid #333", margin: "18px 0" }} />
        <div style={{ fontSize: 13, lineHeight: 1.5, color: "#bcbcbc" }}>
          <strong style={{ color: "#fff" }}>Calidad primero.</strong> Solo cuentan aportes aceptados por moderación. Las fotos aportan cuando forman parte de contenido aprobado; el flujo actual procesa privacidad y difumina rostros. Publicidad y recomendaciones orgánicas se almacenan y miden por separado.
        </div>
        {message && <div role="status" style={{ marginTop: 14, padding: 10, background: "#17301f", borderRadius: 9 }}>{message}</div>}
        {error && <div role="alert" style={{ marginTop: 14, padding: 10, background: "#35191c", borderRadius: 9 }}>{error}</div>}
      </div>
    </div>}
  </>;
}
